import React, { useCallback, useMemo } from 'react';

import { Box, IconButton, Link, Tooltip, Typography } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import ErrorIcon from '@material-ui/icons/Error';
import FiberNewIcon from '@material-ui/icons/FiberNew';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import InfoIcon from '@material-ui/icons/Info';
import LockIcon from '@material-ui/icons/Lock';
import PlayArrowOutlinedIcon from '@material-ui/icons/PlayArrowOutlined';
import StopOutlinedIcon from '@material-ui/icons/StopOutlined';
import WarningIcon from '@material-ui/icons/Warning';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { ReactComponent as MoreIcon } from 'assets/icons/more.svg';
import ContextMenu from 'components/ContextMenu/ContextMenu';
import Table from 'components/Table';
import { actionTemplatesApi } from 'config/api/actionLibrary/actionLibrary';
import type { ActionTemplate } from 'config/api/actionLibrary/actionLibrary.types';
import action_library_messages from 'messages/action_library_messages';
import useActionTemplateCopy from 'pages/ActionLibrary/hooks/useActionTemplateCopy';
import useIsActionTemplateOwner from 'pages/ActionLibrary/hooks/useIsActionTemplateOwner';
import { useConfirmationModalContext } from 'reactContext/ConfirmationModalContext/ConfirmationModalContext';
import PATHS from 'router/PATHS';
import { TableDataMappingRow } from 'types/Table';

import useStyles from './ActionTemplatesTable.styles';

type Props = {
  data: any[] | null;
  loading: boolean;
  sort: { key: string | null; direction: string | null };
  onSort: (key: string, direction: string) => void;
  refreshData: () => void;
  filtersService?: any;
  onSearch?: (searchTerm: string) => void;
  searchValue?: string;
};

const ActionTemplatesTable: React.FC<Props> = ({ data, loading, sort, onSort, refreshData, filtersService, onSearch, searchValue }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const history = useHistory();
  const { showConfirmationModal } = useConfirmationModalContext();
  const { enqueueSnackbar } = useSnackbar();
  const { copyActionTemplate } = useActionTemplateCopy();
  const isActionTemplateOwner = useIsActionTemplateOwner();

  const parsedData = useMemo(() => {
    if (!data) return null;
    return data;
  }, [data]);

  const isLocked = useCallback(
    (template: ActionTemplate) => {
      if (isActionTemplateOwner(template)) return false;
      return !!template.isLocked;
    },
    [isActionTemplateOwner],
  );

  const handleRowClick = (event: React.MouseEvent, template: ActionTemplate) => {
    history.push(PATHS.ACTION_LIBRARY_DETAIL.replace(':id', template.id.toString()));
  };

  const handleDelete = useCallback(
    async (template: ActionTemplate) => {
      const confirmed = await showConfirmationModal({
        title: t(action_library_messages.delete_confirmation.title),
        body: t(action_library_messages.delete_confirmation.body),
      });

      if (confirmed) {
        try {
          await actionTemplatesApi.deleteActionTemplate(template.id);
          enqueueSnackbar(t(action_library_messages.success.deleted), { variant: 'success' });
          refreshData();
        } catch (error) {
          enqueueSnackbar('Failed to delete template', { variant: 'error' });
        }
      }
    },
    [showConfirmationModal, t, enqueueSnackbar, refreshData],
  );

  const handleCopy = useCallback(
    async (listTemplate: ActionTemplate) => {
      await copyActionTemplate(listTemplate);
    },
    [copyActionTemplate],
  );

  const handleToggleActive = useCallback(
    async (template: ActionTemplate) => {
      try {
        const newStatus = template.status === 'active' ? 'inactive' : 'active';
        await actionTemplatesApi.updateActionTemplate(template.id, { status: newStatus });
        enqueueSnackbar(
          newStatus === 'active' ? t(action_library_messages.success.activated) : t(action_library_messages.success.deactivated),
          { variant: 'success' },
        );
        refreshData();
      } catch (error) {
        enqueueSnackbar('Failed to update template status', { variant: 'error' });
      }
    },
    [enqueueSnackbar, t, refreshData],
  );

  const handleEdit = useCallback(
    (template: ActionTemplate) => {
      history.push(PATHS.ACTION_LIBRARY_EDIT.replace(':id', template.id.toString()));
    },
    [history],
  );

  const renderContextMenu = useCallback(
    (template: ActionTemplate) => {
      const elements = [
        {
          label: template.status === 'active' ? t(action_library_messages.actions.deactivate) : t(action_library_messages.actions.activate),
          onClick: () => handleToggleActive(template),
          icon: template.status === 'active' ? <StopOutlinedIcon fontSize='small' /> : <PlayArrowOutlinedIcon fontSize='small' />,
        },
        {
          label: t(action_library_messages.actions.edit),
          onClick: () => handleEdit(template),
          disabled: isLocked(template),
          icon: <EditOutlinedIcon fontSize='small' />,
        },
        {
          label: t(action_library_messages.actions.copy),
          onClick: () => handleCopy(template),
          icon: <FileCopyOutlinedIcon fontSize='small' />,
        },
        {
          label: t(action_library_messages.actions.delete),
          onClick: () => handleDelete(template),
          disabled: isLocked(template),
          icon: <DeleteOutlinedIcon fontSize='small' />,
        },
      ];

      return (
        <ContextMenu elements={elements} id={`action-template-${template.id}`}>
          <IconButton>
            <MoreIcon />
          </IconButton>
        </ContextMenu>
      );
    },
    [handleToggleActive, handleEdit, handleCopy, handleDelete, t],
  );

  const renderLockedIcon = (template: ActionTemplate) => (
    <div style={{ marginRight: 8, display: 'flex' }}>
      {template.lockedByUser ? (
        <Tooltip
          interactive
          placement='right'
          title={
            <Box>
              <Typography style={{ fontWeight: 'bold', display: 'block' }} variant='caption'>
                Edited and locked by: {template.lockedByUser.fullName}
              </Typography>
              <Link
                color='inherit'
                href={`mailto:${template.lockedByUser.email}`}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                variant='caption'
              >
                {template.lockedByUser.email}
              </Link>
            </Box>
          }
        >
          <LockIcon color='error' style={{ fontSize: 16 }} />
        </Tooltip>
      ) : (
        <Tooltip title='Locked'>
          <LockIcon color='error' style={{ fontSize: 16 }} />
        </Tooltip>
      )}
    </div>
  );

  const dataMapping: TableDataMappingRow<ActionTemplate>[] = useMemo(
    () => [
      {
        id: 'name',
        width: '30%',
        label: t(action_library_messages.table.name),
        get: (template: ActionTemplate) => (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {template.isFreshChange && (
              <Tooltip title={t(action_library_messages.table.fresh_changes_tooltip)}>
                <FiberNewIcon color='secondary' fontSize='small' style={{ marginRight: 8 }} />
              </Tooltip>
            )}
            {template.isLocked && renderLockedIcon(template)}
            {template.name}
          </div>
        ),
      },
      {
        id: 'purpose',
        width: '15%',
        label: t(action_library_messages.table.purpose),
        get: (template: ActionTemplate) => template.purpose,
      },
      {
        id: 'area',
        width: '12%',
        label: t(action_library_messages.table.area),
        get: (template: ActionTemplate) => template.areaData?.displayName || template.area,
      },
      {
        id: 'sub_area',
        width: '12%',
        label: t(action_library_messages.table.subarea),
        get: (template: ActionTemplate) => template.subAreaData?.name || template.subArea || '-',
      },
      {
        id: 'status',
        width: '10%',
        label: t(action_library_messages.table.status),
        get: (template: ActionTemplate) => (
          <div className={styles.statusCell}>
            {template.status === 'active' ? (
              <CheckCircleIcon className={`${styles.statusIcon} ${styles.activeIcon}`} />
            ) : (
              <CancelIcon className={`${styles.statusIcon} ${styles.inactiveIcon}`} />
            )}
            <span>{t(action_library_messages.status[template.status])}</span>
          </div>
        ),
      },
      {
        id: 'criticality',
        width: '10%',
        label: t(action_library_messages.table.criticality),
        get: (template: ActionTemplate) => {
          const criticalityKey = template.criticality.toUpperCase() as 'LOW' | 'MEDIUM' | 'HIGH';
          let IconComponent;
          let iconClass;

          if (template.criticality === 'low') {
            IconComponent = InfoIcon;
            iconClass = styles.criticalityLowIcon;
          } else if (template.criticality === 'medium') {
            IconComponent = WarningIcon;
            iconClass = styles.criticalityMediumIcon;
          } else {
            IconComponent = ErrorIcon;
            iconClass = styles.criticalityHighIcon;
          }

          return (
            <div className={styles.statusCell}>
              <IconComponent className={`${styles.statusIcon} ${iconClass}`} />
              <span>{t(action_library_messages.criticality[criticalityKey])}</span>
            </div>
          );
        },
      },
      {
        id: 'creator',
        width: '10%',
        label: t(action_library_messages.table.creator),
        get: (template: ActionTemplate) => {
          const creatorKey = template.ownerType === 'pocketsafe' ? 'PocketSafe' : 'Organization';
          return t(action_library_messages.creator[creatorKey]);
        },
      },
      {
        id: 'actions',
        width: '6%',
        label: '',
        isAddon: true,
        get: (template: ActionTemplate) => renderContextMenu(template),
      },
    ],
    [t, renderContextMenu, styles],
  );

  return (
    <>
      <Table
        clickableRow
        data={parsedData}
        dataMapping={dataMapping}
        enableSearch={!!onSearch}
        filtersService={filtersService}
        onRowClick={handleRowClick}
        onSearch={onSearch}
        onSort={onSort}
        refetching={loading}
        searchValue={searchValue}
        sort={sort}
      />
    </>
  );
};

export default ActionTemplatesTable;
